import { customElement, query, state } from 'lit/decorators.js';
import { fireQuickErrorToast } from '../../../webmodule-common/other/toast-away';
import { EventTemplate } from '../../../webmodule-common/other/ui/events';
import { FabricationJobContainerManager } from '../data/fabrication-job-container';
import { FabricationJobItem } from '../../api/fabrication-job-api';
import { FrameValidationType } from '../../../webmodule-common/interop/webmodule-interop';
import { getApiFactory } from '../../api/api-injector';
import { getFrameDetailsTemplate } from '../../frames/ui';
import { getV6QuoteItemFrameDimensions } from '../../v6config/v6-ui';
import {
  fetchQuoteAsset,
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  isV6
} from '../../quotes/data/v6/helper-functions';
import { highestValidationRank, rankToValidationType } from '../../v6config/validation-rank';
import { html, TemplateResult } from 'lit';
import { interpolateExternal } from '../../../localization/global';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { LangFabricationJobItemsTable } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-items-table-lang';
import { isFrame, quoteItemThumbSrc } from '../../quotes/data/quote-helper-functions';
import { isValidV6ConfigVersion } from '../../../webmodule-common/interop/interfaces/v6-config';
import { nothing } from 'lit';
import { QuoteApi } from '../../api/quote-api';
import { QuoteItem } from '../../api/dealer-api-interface-quote';
import { QuoteItemContainer } from '../../quotes/data/quote-item-container';
import { resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { resourceQuote } from '../../quotes/ui/launcher';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef
} from '../../../webmodule-common/components/src/webmodule-components';

export interface FabricationJobItemsTableOptions {
  containerManager: FabricationJobContainerManager;
  isReadonly: () => boolean;
  onItemClick?: (row: JobItemRow) => Promise<void>;
}

export interface JobItemRow {
  fabricationJobItem: FabricationJobItem;
  quoteItem?: QuoteItem;
  quoteItemContainer?: QuoteItemContainer;
  quoteNumber?: number;
  quoteItemNumber?: number;
  dimensions?: string;
}

@customElement('wm-fabricationjobitemstable')
export class FabricationJobItemsTable extends ViewBase {
  private options: FabricationJobItemsTableOptions;
  private quoteApi: QuoteApi = getApiFactory().quote();
  @state() private rows: JobItemRow[] = [];
  @state() private loading = false;
  private removing = false;
  @state() private _columns: WebModuleLitTableColumnDef[] = [];
  @query('#fab-job-items-table') private table?: WebModuleLitTable;

  constructor(options: FabricationJobItemsTableOptions) {
    super();
    this.options = options;
  }

  get containerManager(): FabricationJobContainerManager {
    return this.options.containerManager;
  }

  get items(): FabricationJobItem[] {
    return this.containerManager.items;
  }

  public async afterConstruction(): Promise<void> {
    this._columns = this.getColumns();
    await this.loadCrossServiceData();
  }

  public async refreshData(): Promise<void> {
    await this.loadCrossServiceData();
  }

  private async loadCrossServiceData(): Promise<void> {
    this.loading = true;
    this.requestUpdate();

    const quoteItemIds = this.items.map(i => i.quoteItemId);
    if (quoteItemIds.length === 0) {
      this.rows = [];
      this.loading = false;
      this.requestUpdate();
      return;
    }

    try {
      const [quoteItemsResult, quoteNumbersResult] = await Promise.all([
        this.quoteApi.getQuoteItemsSummaryById({ quoteItemId: quoteItemIds }),
        this.quoteApi.getQuoteNumbersByQuoteItemIds({ quoteItemIds })
      ]);

      const quoteItemMap = new Map<string, { item: QuoteItem; container: QuoteItemContainer }>();
      if (quoteItemsResult?.items) {
        for (const qi of quoteItemsResult.items) {
          const providerData = quoteItemsResult.data?.find(d => d.id === qi.id);
          const price = quoteItemsResult.prices?.find(p => p.id === qi.id) ?? null;
          quoteItemMap.set(qi.id, {
            item: qi,
            container: { item: qi, data: providerData ?? null, price: price as any }
          });
        }
      }

      // Hydrate provider data from asset paths (V6 frame data is stored as assets)
      await Promise.all(
        Array.from(quoteItemMap.values()).map(async ({ item, container }) => {
          if (container.data && !container.data.providerData) {
            const dataPath = container.data.assetVirtualPaths?.['data'];
            if (dataPath?.path) {
              try {
                const providerData = await fetchQuoteAsset({
                  quoteId: item.quoteId,
                  quoteItemId: item.id,
                  assetPath: dataPath.path,
                  publicUrl: dataPath.publicUrl
                });
                container.data.providerData = providerData as string;
              } catch {
                // Non-blocking — options/dimensions will show as empty
              }
            }
          }
        })
      );

      const quoteNumberMap = new Map<string, number>();
      const quoteIdMap = new Map<string, string>();
      if (quoteNumbersResult?.items) {
        for (const qn of quoteNumbersResult.items) {
          quoteNumberMap.set(qn.quoteItemId, qn.quoteNumber);
          quoteIdMap.set(qn.quoteItemId, qn.quoteId);
        }
      }

      // Compute quote item position: group items by quoteId, assign index+1
      const quoteItemPositionMap = new Map<string, number>();
      const itemsByQuote = new Map<string, string[]>();
      if (quoteItemsResult?.items) {
        for (const qi of quoteItemsResult.items) {
          const qId = quoteIdMap.get(qi.id) ?? qi.quoteId;
          if (!itemsByQuote.has(qId)) itemsByQuote.set(qId, []);
          itemsByQuote.get(qId)!.push(qi.id);
        }
        for (const [, itemIds] of itemsByQuote) {
          itemIds.forEach((id, idx) => quoteItemPositionMap.set(id, idx + 1));
        }
      }

      this.rows = this.items.map(fabItem => {
        const quoteData = quoteItemMap.get(fabItem.quoteItemId);
        const quoteNumber = quoteNumberMap.get(fabItem.quoteItemId);
        const quoteItemNumber = quoteItemPositionMap.get(fabItem.quoteItemId);
        let dimensions = '';
        if (quoteData?.container) {
          const v6Item = getV6QuoteItemForContainer(quoteData.container);
          if (v6Item) dimensions = getV6QuoteItemFrameDimensions(v6Item);
        }
        return {
          fabricationJobItem: fabItem,
          quoteItem: quoteData?.item,
          quoteItemContainer: quoteData?.container,
          quoteNumber,
          quoteItemNumber,
          dimensions
        };
      });
    } catch (e) {
      console.error('Failed to load cross-service data for fabrication job items', e);
      fireQuickErrorToast(LangFabricationJobItemsTable.loadCrossServiceDataFailed());
      this.rows = this.items.map(fabItem => ({ fabricationJobItem: fabItem }));
    }

    this.loading = false;
    this.requestUpdate();
  }

  private getColumns(): WebModuleLitTableColumnDef[] {
    const cols: WebModuleLitTableColumnDef[] = [];

    // Item #
    cols.push({
      title: () => LangFabricationJobItemsTable.itemNumberTitle(),
      classes: 'colpxmax-48',
      fieldName: 'id',
      displayValue: (_table: WebModuleLitTable, _item: unknown, index: number) => {
        return html`${index + 1}`;
      }
    });

    // Quote # + Quote Item Id
    cols.push({
      title: () => LangFabricationJobItemsTable.quoteNumberTitle(),
      classes: 'colpxmax-70 quote-item-title',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        const qi = row.quoteItem;
        const quoteLink = row.quoteNumber
          ? html`<a class="quote-item-link" href="${resolveURL(resourceQuote, qi?.quoteId ?? '')}"
              >${LangFabricationJobItemsTable.quoteNumberLink(row.quoteNumber)}</a
            >`
          : html`<span>—</span>`;
        const itemNum = row.quoteItemNumber
          ? html`<span class="quote-item-description text-muted"
              >${LangFabricationJobItemsTable.itemNumberLabel(row.quoteItemNumber)}</span
            >`
          : html``;
        return html`${quoteLink}${itemNum}`;
      }
    });

    // Image
    cols.push({
      title: () => LangFabricationJobItemsTable.imageTitle(),
      classes: 'colpxmax-64 quote-item-image',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        const qi = row.quoteItem;
        if (!qi) return html``;

        const src =
          qi.virtualThumbnailPath === ''
            ? quoteItemThumbSrc(qi)
            : this.quoteApi.convertQuoteItemAssetPathToUrl(
                qi.quoteId,
                qi.id,
                qi.virtualThumbnailPath,
                qi.recordVersion
              );

        return html`<img class="quote-item-thumbnail" alt="" src="${src}" style="width:48px; height:48px" />`;
      }
    });

    // Location / Description
    cols.push({
      title: () => LangFabricationJobItemsTable.locationDescriptionTitle(),
      classes: 'colpx-240 quote-item-title',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        const qi = row.quoteItem;
        const title = qi?.title || row.fabricationJobItem.quoteItemId.substring(0, 8);
        const clickEvent = (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          this.options.onItemClick?.(row);
        };
        return html`<a class="quote-item-link" href="#" @click=${clickEvent}>${title}</a>
          <span class="quote-item-description">${qi?.description ?? ''}</span>`;
      }
    });

    // Options (dimensions + expand/collapse)
    const expandButton = html`
      <span>${LangFabricationJobItemsTable.optionsTitle()}</span>
      <div>
        <webmodule-icon
          id="fab-job-expand-all-indicator"
          library="fa"
          name="fas-angles-down"
          @click=${() => this.toggleAllExpanded()}
        ></webmodule-icon>
      </div>
    `;

    cols.push({
      title: html`${expandButton}`,
      classes: 'colpx-160 quote-item-description',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        if (!row.quoteItemContainer) return html`${row.dimensions || '—'}`;

        const oneLiner = this.getOptionsOneLiner(row.quoteItemContainer);
        const isExpanded = _table.isExpanded(row) != undefined;
        const caret = isExpanded ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down';
        return html`<span>${oneLiner}</span><span class="float-end options-dropdown"><i class="${caret}"></i></span>`;
      },
      click: (e: Event, table: WebModuleLitTable, item: unknown) => {
        e.stopPropagation();
        e.preventDefault();
        const row = item as JobItemRow;
        if (!row.quoteItemContainer) return false;

        if (table.isExpanded(row)) {
          table.remExtension(row);
        } else {
          table.addExtension(row, this.extendedDetailsGenerator(row));
        }
        return true;
      }
    });

    // Site Overrides
    cols.push({
      title: () => LangFabricationJobItemsTable.siteOverridesTitle(),
      classes: 'colpx-200',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        const quantity = row.quoteItem?.quantity ?? 0;
        const overrideCount =
          row.fabricationJobItem.unitOverrides?.filter(o => o.v6QuoteItemVirtualPath !== null).length ?? 0;
        const clickEvent = (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
          this.options.onItemClick?.(row);
        };
        if (quantity <= 0) return html`—`;
        if (overrideCount === 0)
          return html`<a class="quote-item-link text-muted" href="#" @click=${clickEvent}
            >${LangFabricationJobItemsTable.noOverrides()}</a
          >`;
        return html`<a class="quote-item-link" href="#" @click=${clickEvent}
          >${LangFabricationJobItemsTable.unitsHaveOverrides(overrideCount, quantity)}</a
        >`;
      }
    });

    // Tags
    cols.push({
      title: () => LangFabricationJobItemsTable.tagsTitle(),
      classes: 'colpxmax-64 quote-item-tags no-pseudo',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        if (!row.quoteItemContainer) return html``;
        return this.getItemTags(row.quoteItemContainer);
      }
    });

    // Qty
    cols.push({
      title: () => LangFabricationJobItemsTable.qtyTitle(),
      classes: 'colpxmax-48',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        return html`${row.quoteItem?.quantity ?? '—'}`;
      }
    });

    // Actions (hamburger menu)
    cols.push({
      title: () => '',
      classes: 'colpxmax-50 no-pseudo',
      fieldName: 'xx',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const row = item as JobItemRow;
        const isReadonly = this.options.isReadonly() || !this.containerManager.isDraft;
        const clickView = (e: Event) => {
          e.stopPropagation();
          this.options.onItemClick?.(row);
        };
        const clickDelete = (e: Event) => {
          e.stopPropagation();
          this.removeItem(row);
        };
        return html`<webmodule-dropdown class="item-menu text-start" placement="bottom-end" hoist>
          <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
          <webmodule-menu>
            <webmodule-menu-item @click=${clickView}>
              <webmodule-icon slot="prefix" library="fa" name="fas-pen-to-square"></webmodule-icon>
              ${LangFabricationJobItemsTable.editMenuItem()}
            </webmodule-menu-item>
            ${!isReadonly
              ? html`<webmodule-menu-item @click=${clickDelete}>
                  <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
                  ${LangFabricationJobItemsTable.removeMenuItem()}
                </webmodule-menu-item>`
              : html``}
          </webmodule-menu>
        </webmodule-dropdown>`;
      }
    });

    return cols;
  }

  private getOptionsOneLiner(container: QuoteItemContainer): string {
    if (!isV6(container)) return '';
    const data = getV6QuoteItemForContainer(container);
    return getV6QuoteItemFrameDimensions(data);
  }

  private extendedDetailsGenerator(row: JobItemRow) {
    return async () => {
      const container = row.quoteItemContainer;
      if (!container) return html``;
      return this.getExtendedDetails(container);
    };
  }

  private getExtendedDetails(container: QuoteItemContainer): TemplateResult {
    if (!isFrame(container.item) || !isV6(container)) {
      return this.getNotesTemplate(container);
    }

    if (!isValidV6ConfigVersion()) return html``;

    const data = getV6FrameForContainer(container);

    const validationsTemplate = (validationType: FrameValidationType, cssClass: string): TemplateResult => {
      const items = data?.validations?.filter(x => x.type === validationType) ?? [];
      if (items.length === 0) return html``;
      return html`
        <ul class="list-group">
          ${items.map(
            issue =>
              html`<li class="list-group-item ${cssClass}">
                <span class="me-3 fw-bold">${interpolateExternal(issue.title)}</span>${interpolateExternal(
                  issue.text ?? ''
                )}
              </li>`
          )}
        </ul>
      `;
    };

    return html`<div class="extended-options-line small-font main-body">
      ${data ? getFrameDetailsTemplate(data, true) : nothing}
      ${validationsTemplate(FrameValidationType.critical, 'list-group-item-danger')}
      ${validationsTemplate(FrameValidationType.warning, 'list-group-item-warning')}
      ${validationsTemplate(FrameValidationType.information, 'list-group-item-info')}
      ${validationsTemplate(FrameValidationType.note, 'list-group-item-note')} ${this.getNotesTemplate(container)}
    </div>`;
  }

  private getNotesTemplate(container: QuoteItemContainer): TemplateResult {
    const hasComment = !isEmptyOrSpace(container.item.comment);
    const hasManufacturerComment = !isEmptyOrSpace(container.item.commentToManufacturer);
    if (!hasComment && !hasManufacturerComment) return html``;

    return html`<ul class="list-group small-font">
      ${hasComment
        ? html`<li class="list-group-item list-group-item-primary">
            <span class="me-3 fw-bold">${LangFabricationJobItemsTable.notesToClient()}</span> ${container.item.comment}
          </li>`
        : nothing}
      ${hasManufacturerComment
        ? html`<li class="list-group-item list-group-item-primary">
            <span class="me-3 fw-bold">${LangFabricationJobItemsTable.notesToManufacturer()}</span> ${container.item
              .commentToManufacturer}
          </li>`
        : nothing}
    </ul>`;
  }

  private getItemTags(container: QuoteItemContainer): TemplateResult {
    const validationIndicator = () => {
      if (!isV6(container)) return html``;
      const data = getV6FrameForContainer(container);
      if (!data) return html``;
      if (!data.validations?.length) return html``;

      const rank = highestValidationRank(data.validations);
      const state = rankToValidationType(rank);
      switch (state) {
        case FrameValidationType.note:
          return html`<webmodule-icon library="fa" name="fas-file-lines" class="text-blue"></webmodule-icon>`;
        case FrameValidationType.information:
          return html`<webmodule-icon library="fa" name="fas-circle-info" class="text-cyan"></webmodule-icon>`;
        case FrameValidationType.warning:
          return html`<webmodule-icon
            library="fa"
            name="fas-triangle-exclamation"
            class="text-yellow"
          ></webmodule-icon>`;
        case FrameValidationType.critical:
          return html`<webmodule-icon library="fa" name="fas-circle-exclamation" class="text-red"></webmodule-icon>`;
      }
      return html``;
    };

    const notesIndicator = () => {
      if (container.item.comment)
        return html`<webmodule-icon library="fa" name="fas-message" class="text-green"></webmodule-icon>`;
      return html``;
    };

    const notesManufacturerIndicator = () => {
      if (container.item.commentToManufacturer)
        return html`<webmodule-icon library="fa" name="fas-message" class="text-black"></webmodule-icon>`;
      return html``;
    };

    return html`${notesIndicator()}${notesManufacturerIndicator()}${validationIndicator()}`;
  }

  private toggleAllExpanded() {
    if (!this.table) return;
    const anyExpanded = this.rows.some(row => this.table!.isExpanded(row));
    this.rows.forEach(row => {
      if (anyExpanded) {
        if (this.table!.isExpanded(row)) this.table!.remExtension(row);
      } else {
        if (!this.table!.isExpanded(row) && row.quoteItemContainer) {
          this.table!.addExtension(row, this.extendedDetailsGenerator(row));
        }
      }
    });
  }

  private async removeItem(row: JobItemRow): Promise<void> {
    if (this.removing) return;
    this.removing = true;
    try {
      await this.containerManager.removeItem(row.fabricationJobItem.quoteItemId);
      await this.loadCrossServiceData();
    } finally {
      this.removing = false;
    }
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected template(): EventTemplate {
    if (this.loading) {
      return html`<div class="p-3 text-muted">${LangFabricationJobItemsTable.loadingItems()}</div>`;
    }

    return html`<webmodule-lit-table
      id="fab-job-items-table"
      .rowClass=${'tr'}
      .colClass=${'column'}
      .columns=${this._columns as any}
      .data=${this.rows}
      .clickrows=${false}
      .keyevent=${(row: JobItemRow) => row.fabricationJobItem.quoteItemId}
      .rowidevent=${(row: JobItemRow) => row.fabricationJobItem.quoteItemId}
      tablestyle="nestedtable"
      pageLength="100"
    ></webmodule-lit-table>`;
  }
}
